import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { getLocalStorage } from '../aaa';
import { getPublicMediaRoot } from '../serviceAgent';
import { logger } from '../error-tracker';
import { RootState } from '../ApplicationState';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { SpinnerOverlay } from '../SpinnerOverlay';
import StudentHeroSegment from '../components/StudentHeroSegment';
import MiddleSchoolBackButton from '../components/MiddleSchoolBackButton';
import ToDoContent from '../components/ToDoContent';
import ToDoFeaturedStory from '../components/ToDoFeaturedStory';
import { getMiddleSchoolModule, retrieveAssignment } from '../BackendInterface';
import Analytics from '../Analytics';
import { getModuleDuration, getModuleIcon, logDebugLevel, secondarySchoolLessonNumberLookup } from '../GlobalFunctions';
import styles from './ToDoContainer.module.scss';
import { Icon, IconifyIcon, InlineIcon } from '@iconify/react';
import clockOutline from '@iconify/icons-eva/clock-outline';
import { connect } from 'react-redux';
import { Assignment, EmbeddedContent } from '@peekapak/types/LessonPlanTypes';
import { UserType } from '@peekapak/types/DataProtocolTypes';
import { useErrorStatus } from '../components/EnableErrorMessagePopup/EnableErrorMessagePopup';

interface urlParams {
  toDo: string;
  grade: string;
  language: string;
  activity: string;
}

interface Props {
  setCurrentToDo: React.Dispatch<React.SetStateAction<Assignment>>;
  setGroupedToDoInfo: React.Dispatch<React.SetStateAction<Assignment[]>>;
  groupedToDoInfo: Assignment[];
  currentToDo: { unitDisplayName: string; lesson: string };
  userProfile: UserProfileType;
}

interface TabData {
  title: string;
  type: string;
  content: EmbeddedContent[];
  representativeImage: string;
}

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
});

const ToDoContainer = ({ currentToDo, setCurrentToDo, setGroupedToDoInfo, groupedToDoInfo, userProfile }: Props) => {
  const [thisModule, setThisModule] = useState(null);
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [tabData, setTabData] = useState<TabData[]>([]);
  const [isModuleLoaded, setIsModuleLoaded] = useState(false);
  const [isTodoLoaded, setIsTodoLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [headerImagePath, setHeaderImagePath] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const isSmall = useMediaQuery({ maxWidth: 576 });
  const onShowErrorMessage = useErrorStatus();

  const history = useHistory();
  const { activity, language, grade, toDo }: urlParams = useParams();
  const isHighSchool = parseInt(grade) > 8;
  const isRtl = language === 'ar';

  const unitDisplayName = currentToDo?.unitDisplayName || '';
  const lesson = currentToDo?.lesson || '';

  const getActivityType = (type: string) => {
    if (type === 'questionAnswer') return 'Respond';
    if (type === 'Story' || type === 'Featured Story') return 'Story';
    if (type === 'multipleChoice') return 'Answer';
    if (type === 'resource') return 'Materials';
    return 'Respond';
  };

  const getToDoType = (toDo: { type: string; content: { type: string }[] }) => {
    if (toDo.type === 'textBlock') return 'Featured Story';
    if (toDo.content[0]?.type === 'multipleChoice') return 'multipleChoice';
    if (toDo.content.some((obj) => obj.type === 'questionAnswer')) return 'questionAnswer';
    if (toDo.content.some((obj) => obj.type === 'imageCaptureBlock')) return 'imageUpload';
    if (toDo.content.some((obj) => obj.type === 'slideDeck')) return 'resource';
    if (toDo.content.some((obj) => obj.type === 'reflectionSurvey')) return 'reflectionSurvey';
    if (toDo.content[0]?.type === 'textBlock') return 'Featured Story';
    else return 'resource';
  };

  const handleTabClick = (tab: number) => {
    setIsTodoLoaded(false);
    setActiveTab(tab);
    if ((tab === 1 || tab === 2) && groupedToDoInfo) {
      history.push(
        `/studentportal/activity/${activity}/${language}/${grade}/toDo/${groupedToDoInfo[tab - 1].createdAt}`,
      );
      setCurrentToDo(groupedToDoInfo[tab - 1]);
    }
  };

  const goToLastTab = () => {
    const lastTab = tabData[tabData?.length - 1];
    setActiveTab(lastTab);
  };

  const getModule = useCallback(async () => {
    try {
      const modulePayload = await getMiddleSchoolModule(activity, language, grade);
      setThisModule(modulePayload.data);
      const { moduleContent, studentContent } = modulePayload.data;
      const filteredStudentContent = (studentContent || []).filter((item: { content: { grade?: number[] }[] }) => {
        if (!item.content || item.content.length === 0) return false;
        return item.content.some(
          (contentItem) => contentItem.grade === undefined || contentItem.grade.includes(+grade),
        );
      });
      const combinedContent = moduleContent ? moduleContent.concat(filteredStudentContent) : filteredStudentContent;
      setTabData(combinedContent);
    } finally {
      setIsModuleLoaded(true);
    }
  }, [activity, grade, language]);

  const getToDo = useCallback(async () => {
    try {
      const { userId } = userProfile;
      const createdAt = parseInt(toDo);
      if (Number.isNaN(createdAt)) {
        logDebugLevel('ToDoContainer', 'getToDo', `Looks like this is a teacher preview of assignment contents`);
      } else {
        const toDoPayload = await retrieveAssignment(userId, createdAt);
        setAssignment(toDoPayload[0]);
        const scrollPosition = isSmall ? 608 : 490;
        window.scrollTo(0, scrollPosition);
      }
    } catch {
      console.error('Student work does not exist for this assignment');
    } finally {
      setIsTodoLoaded(true);
    }
  }, [isSmall, toDo, userProfile]);

  const updateAssignment = useCallback((updatedAssignment: Assignment) => {
    setAssignment(updatedAssignment);
  }, []);

  useEffect(() => {
    if (!isModuleLoaded) getModule();
    if (!isTodoLoaded) getToDo();
    const headerImage = thisModule?.featuredImage;
    const headerImagePath = `${getPublicMediaRoot()}images/secondary/${headerImage}`;
    setHeaderImagePath(headerImagePath);
  }, [tabData, assignment, activeTab, isModuleLoaded, isTodoLoaded, getModule, getToDo, thisModule]);

  useEffect(() => {
    const storedToDo = getLocalStorage('currentToDo');
    setCurrentToDo(storedToDo);
    const storedGroupedToDo = getLocalStorage('groupedToDoInfo');
    setGroupedToDoInfo(storedGroupedToDo);
  }, [toDo, setCurrentToDo, setGroupedToDoInfo]);

  useEffect(() => {
    if (tabData.length > 0 && Number.isNaN(parseInt(toDo))) {
      const tabIndex = tabData.findIndex((tab: Record<string, any>) => {
        if (!tab.id) return activity === toDo;
        return tab.id === toDo;
      });
      if (tabIndex >= 0) {
        setActiveTab(tabIndex);
      }
    }
  }, [tabData, toDo, activity]);

  useEffect(() => {
    if (isModuleLoaded && isTodoLoaded) {
      setIsLoaded(true);
      const story = tabData?.[activeTab]?.type === 'textBlock' && tabData?.[activeTab]?.title;
      if (story != false) {
        if (userProfile.type === UserType.STUDENT) {
          Analytics.journalAssignmentStoryOpen(story, userProfile);
        } else {
          Analytics.lessonsStoryOpen(story, userProfile);
        }
      } else {
        Analytics.journalAssignmentOpen(activity, parseInt(toDo), userProfile);
      }
    }
  }, [isModuleLoaded, isTodoLoaded, activeTab, activity, tabData, toDo, userProfile]);
  return (
    <>
      <SpinnerOverlay isShow={!isLoaded} />
      <StudentHeroSegment backgroundImage={headerImagePath}>
        <div className={styles.heroText}>
          <div className={styles.backButton}>
            <MiddleSchoolBackButton />
          </div>
          <h3>To Dos</h3>
          {currentToDo && (
            <h3 className={styles.darkBlue}>
              {unitDisplayName} &#8226;&nbsp;Lesson {secondarySchoolLessonNumberLookup(lesson, isHighSchool)}:{' '}
              {currentToDo.lessonTitle ?? lesson}
            </h3>
          )}
          {isLoaded && (
            <>
              <h1>{tabData[activeTab].title}</h1>
              {thisModule && (thisModule.duration || thisModule.durationMin || thisModule.durationMax) && (
                <h4>
                  <InlineIcon icon={clockOutline} />
                  Estimated time {getModuleDuration(thisModule)}
                </h4>
              )}
            </>
          )}
        </div>
      </StudentHeroSegment>
      <div className={styles.contentContainer}>
        <div className={styles.sticky}>
          <div className={styles.tabRow}>
            {tabData?.map((tab: TabData, index: number) => {
              const type = getToDoType(tab);

              if (!tab?.content || tab?.content.length === 0) {
                const errorMessage = `<p>We could not find content for assignment. Is the current user in the correct grade to use this module?</p> \
<p>Module: ${activity}, Language: ${language}, User's Grade: ${userProfile.grade} Classroom: ${assignment?.classroomId || 'Unknown'}</p>\
<p>Please contact Peekapak Support with a copy of this message for assistance.</p>`;
                onShowErrorMessage(errorMessage, `There's a problem...`, () => history.push('/'));
                logger.logException(new Error(errorMessage));
                return null;
              }

              // avoid check in tool being displayed as a tab
              if (tab.content[0].type === 'checkInTool') return null;
              return (
                <button
                  role='tab'
                  key={`tab ${index}`}
                  className={activeTab === index ? styles.activeTab : ''}
                  onClick={() => handleTabClick(index)}
                >
                  <div className={styles.tabText}>
                    <Icon icon={getModuleIcon(type) as IconifyIcon} width={18} className={styles.icon} />
                    {getActivityType(type)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.moduleContent} dir={isRtl ? 'rtl' : 'ltr'}>
          {isModuleLoaded &&
            isTodoLoaded &&
            tabData &&
            tabData.map((tab: TabData, index: number) => {
              const type = getToDoType(tab);
              return activeTab !== index ? null : type === 'Featured Story' ? (
                <ToDoFeaturedStory
                  content={tab}
                  key={`content pane ${index}`}
                  tabNum={index}
                  handleTabClick={handleTabClick}
                  nextTab={index + 1}
                  isLastTab={index + 1 === tabData?.length}
                />
              ) : (
                <ToDoContent
                  goToLastTab={goToLastTab}
                  isMultiTab={tabData?.length > 1}
                  content={tab.content}
                  studentWork={assignment}
                  updateAssignment={updateAssignment}
                  key={`content pane ${index + 1}`}
                  assetImage={tab.representativeImage}
                />
              );
            })}
        </div>
      </div>
    </>
  );
};

export default connect(mapStateToProps, null)(ToDoContainer);
